import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ConfirmacaoContext = createContext(null);

/**
 * Confirmação (07/08/2026, pedido do Juan) — o navegador tem sua
 * própria caixinha de confirmação (window.confirm), usada em 83
 * lugares do sistema — mas ela não segue o visual do SGQ (fonte,
 * cor, botões do navegador, não do sistema), e em alguns navegadores
 * mobile fica ainda mais deslocada.
 *
 * Uso: `const confirmar = useConfirmacao(); ... if (!(await
 * confirmar('Excluir X?'))) return;` — mesma lógica de sempre
 * (window.confirm também retorna true/false), só que via Promise
 * (precisa de await) e desenhado com os componentes do sistema.
 */
export function ConfirmacaoProvider({ children }) {
  const [estado, setEstado] = useState(null); // { mensagem, tituloProprio, textoOk, perigo }
  const [digitado, setDigitado] = useState('');
  const [motivoTx, setMotivoTx] = useState('');   // (V1932.21) o motivo, quando a pergunta pede (o Corrigir o laudo)
  const resolverRef = useRef(null);

  const confirmar = useCallback((mensagem, opcoes = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setEstado({
        mensagem,
        /* Só o título que a chamada passou — o genérico "Confirmar" virou
           rótulo, e a pergunta sai da mensagem (ver `dividirMensagem`). */
        tituloProprio: opcoes.titulo || null,
        /* ══ A CONFIRMAÇÃO v2 (V1904, 12/10/2026 — a Sala de Provas v2, "toda exclusão e remoção em modal"). A MESMA função e o MESMO
           provedor: com `v2`, o modal novo — o alerta, o que se PERDE e o que CONTINUA em frases separadas, o vermelho largo em cima,
           o Cancelar embaixo, e no telefone subindo de baixo. Sem `v2`, o desenho de hoje: as 60 telas que chamam `confirmar` não mudam.
           A antiga sai quando a última delas passar a pedir a v2 (a regra da troca). */
        v2: !!opcoes.v2, perde: opcoes.perde || null, continua: opcoes.continua || null,
        textoOk: opcoes.textoOk || 'Confirmar',
        textoCancelar: opcoes.textoCancelar || 'Cancelar',
        perigo: opcoes.perigo !== false, // maioria dos usos hoje é pra ação destrutiva
        /* ══ NÍVEL 2 · EXCLUSÃO EM CASCATA (25/09/2026, demo-confirmacao-niveis
           aprovada). Três opções a mais, e a caixa é a mesma:
             · `aviso`       — o que some, com os números (texto ou JSX);
             · `avisoPerigo` — o aviso fica vermelho: acesso de pessoa ou dado
                               que não se refaz. Estrutura se refaz, fica neutro;
             · `digitar`     — o texto que a pessoa tem de digitar; o botão só
                               acende quando bate, e `rotuloDigitar` diz o quê.
           ⚠ SEM "MANTER ALGUNS" (25/09, decisão dele): *"não faz sentido
           manter algo que não tem mais a fonte cadastrada no sistema"*. O que
           DEPENDE da fonte vai junto; o que tem vida própria (a pessoa) fica,
           e o aviso diz para onde ela vai. Não há caixa de marcar aqui. */
        aviso: opcoes.aviso || null,
        avisoPerigo: !!opcoes.avisoPerigo,
        digitar: opcoes.digitar != null ? String(opcoes.digitar) : null,
        rotuloDigitar: opcoes.rotuloDigitar || 'Para confirmar, digite o nome',
        motivo: !!opcoes.motivo,
      });
      setDigitado(''); setMotivoTx('');
    });
  }, []);

  const responder = (valor) => {
    resolverRef.current?.(valor === true && estado?.motivo ? motivoTx.trim() : valor);   // (com o motivo, a resposta é o texto dele)
    resolverRef.current = null;
    setEstado(null);
  };

  const { pergunta, corpo } = estado ? dividirMensagem(estado.mensagem, estado.tituloProprio) : {};
  const cascata = !!(estado && (estado.aviso || estado.digitar != null));
  const bate = (!estado?.digitar || digitado.trim() === estado.digitar.trim()) && (!estado?.motivo || motivoTx.trim().length >= 5);

  return (
    /* (V1932.21, ele: "esse modal da confirmação da edição do laudo não tá padrão do sistema") as telas que não são React (a rota da Sala 5) usam esta mesma peça */
    <ConfirmacaoContext.Provider value={(typeof window !== 'undefined' && (window.__sgqConfirmar = confirmar), confirmar)}>
      {children}
      {estado && (
        <div className={estado.v2 ? 'pm-folha mf-folha' : 'modal-overlay'} onClick={() => responder(false)} style={{ zIndex: 2000 }}>
          {/* ⚠ `.modal-p` da escada (16/09/2026). Esta caixa É a família
              inteira de confirmação do sistema: 99 chamadas de `confirmar`
              em 34 arquivos, 9 delas na Sala 1. Padronizar é trocar esta,
              uma vez.

              ⚠⚠ O PADRÃO NOVO (24/09/2026, demo `demo-confirmacao.html`,
              opção B escolhida pelo dono do produto). SEM FAIXA de
              cabeçalho, de propósito: a faixa invertida é da ficha e da
              criação; aqui ela empilharia uma segunda faixa preta sobre a
              ficha de trás e a pergunta pareceria outra tela abrindo.
              Rótulo miúdo, a PERGUNTA como título de frase (17px) e a
              consequência no texto de apoio. */}
          {estado.v2 ? (
            /* ══ A CONFIRMAÇÃO v2, NO DESENHO DA DEMO APROVADA (sala-de-provas-trocas.html, "Confirmar · Agora") — a folha mf-folha com a caixa mf-cf:
               o alerta, a pergunta, o que se PERDE (pode vir com o nome em negrito) e o que CONTINUA, o número digitado no que é grave, o vermelho
               escuro largo (mf-tira) e o Cancelar cinza cheio (pm-cancela). No telefone sobe de baixo; no computador, 460 px no centro. */
            <div className="mf-cf" role="alertdialog" aria-modal="true" aria-labelledby="confirmacao-pergunta" onClick={e => e.stopPropagation()}>
              <span className="mf-cf-al" aria-hidden="true">!</span>
              <b id="confirmacao-pergunta">{pergunta}</b>
              {(estado.perde || corpo) && <p>{estado.perde || corpo}</p>}
              {estado.continua && <p>{estado.continua}</p>}
              {estado.aviso && <div className={`aviso${estado.avisoPerigo ? ' perigo' : ''}`}><span>{estado.aviso}</span></div>}
              {estado.motivo && (
                <label className="mf-cf-c"><small>O motivo</small>
                  <textarea rows={3} value={motivoTx} placeholder="o que aconteceu e por quê (fica no histórico)" onChange={e => setMotivoTx(e.target.value)} /></label>
              )}
              {estado.digitar != null && (
                <label className="mf-cf-c"><small>{estado.rotuloDigitar}</small>
                  <input value={digitado} autoComplete="off" autoFocus placeholder={estado.digitar} onChange={e => setDigitado(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && bate) responder(true); }} /></label>
              )}
              <button type="button" className={estado.perigo ? 'mf-tira gde' : 'mf-cf-ok gde'} disabled={!bate} onClick={() => bate && responder(true)}>{estado.textoOk}</button>
              <button type="button" className="pm-cancela" onClick={() => responder(false)}>{estado.textoCancelar}</button>   {/* sem foco automático: a demo não tem o contorno; o Enter não confirma */}
            </div>
          ) : (
          <div className={`modal modal-p${cascata ? ' modal-cascata' : ''}`} role="alertdialog" aria-modal="true"
            aria-labelledby="confirmacao-pergunta" onClick={e => e.stopPropagation()}>
            <div className="modal-pergunta">
              <span className="fila-cab-r">Confirmar</span>
              <h3 className="fila-cab-t" id="confirmacao-pergunta">{pergunta}</h3>
            </div>
            {corpo && <p className="modal-consequencia">{corpo}</p>}
            {estado.aviso && (
              <div className={`aviso${estado.avisoPerigo ? ' perigo' : ''}`}><span>{estado.aviso}</span></div>
            )}
            {estado.digitar != null && (
              <div className="campo">
                <label className="campo-r" htmlFor="confirmacao-digitar">{estado.rotuloDigitar}</label>
                <input id="confirmacao-digitar" className="input" value={digitado} autoComplete="off" autoFocus
                  placeholder={estado.digitar} onChange={e => setDigitado(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && bate) responder(true); }} />
              </div>
            )}
            {/* ⚠ ESCALA DE AÇÃO (26/08/2026): o destrutivo é `acao-perigo`,
                vermelho sólido — o mesmo do item não conforme. ⚠ As duas
                respostas dividem a linha em partes iguais (24/09): a
                diferença está na cor e na palavra, não no tamanho. */}
            <div className="linha">
              <button type="button" className="acao acao-g acao-contorno linha-corpo"
                onClick={() => responder(false)} autoFocus={estado.digitar == null}>
                {estado.textoCancelar}
              </button>
              <button type="button"
                className={`acao acao-g linha-corpo ${estado.perigo ? 'acao-perigo' : 'acao-principal'}`}
                disabled={!bate} onClick={() => bate && responder(true)}>
                {estado.textoOk}
              </button>
            </div>
          </div>
          )}
        </div>
      )}
    </ConfirmacaoContext.Provider>
  );
}

/* ══ A PERGUNTA E A CONSEQUÊNCIA (24/09/2026) ═══════════════════════════
   ESCOLHA: a divisão é feita AQUI, e não nas 99 chamadas. Medido: 78 das
   88 mensagens literais têm uma pergunta; 78 das 99 chamadas não passam
   título. Reescrever cada uma seria mexer em 34 arquivos para ganhar o que
   uma função entrega.
     1 · a chamada passou `titulo` → ele é a pergunta, a mensagem é o texto;
     2 · há parágrafo em branco → o primeiro é a pergunta;
     3 · há uma frase terminada em "?" → ELA é a pergunta, mesmo que não
         seja a primeira ("Votação online é opcional… Quer mesmo gerar?"),
         e as outras ficam no texto, na ordem em que foram escritas;
     4 · sem "?" → a primeira frase, se for curta;
     5 · nada disso → "Confirmar?" e a mensagem inteira no texto.
   ⚠ Nenhuma palavra é inventada: o que vira título já estava escrito.
   ⚠ A frase termina em pontuação SEGUIDA DE ESPAÇO: "R$ 1.500" não corta. */
const TETO_PERGUNTA = 140;
export function dividirMensagem(mensagem, titulo) {
  const texto = String(mensagem || '').trim();
  if (titulo) return { pergunta: titulo, corpo: texto };
  const par = texto.split(/\n\s*\n/);
  if (par.length > 1 && par[0].length <= TETO_PERGUNTA) {
    return { pergunta: par[0].trim(), corpo: par.slice(1).join('\n\n').trim() };
  }
  const frases = texto.match(/[\s\S]+?(?:[?.!](?=\s|$)|$)/g)?.map(f => f.trim()).filter(Boolean) || [texto];
  const q = frases.findIndex(f => f.endsWith('?'));
  const i = q !== -1 ? q : 0;
  if (frases[i].length <= TETO_PERGUNTA) {
    return { pergunta: frases[i], corpo: frases.filter((_, k) => k !== i).join(' ') };
  }
  return { pergunta: 'Confirmar?', corpo: texto };
}

export const useConfirmacao = () => useContext(ConfirmacaoContext);
